import React, { useState, useRef, useEffect } from 'react';
import StaffScanGate from '@/components/StaffScanGate';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Scan, Loader2, PackageCheck, FileBarChart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const JwReceiptStation = () => {
  const navigate = useNavigate();
  const [usn, setUsn] = useState('');
  const [scanning, setScanning] = useState(false);
  const [summary, setSummary] = useState({}); // jw_id -> {assembly, sent, recv, ret, bal, session}
  const [log, setLog] = useState([]);
  const usnRef = useRef(null);

  useEffect(() => { usnRef.current?.focus(); }, []);

  const scan = async (e) => {
    e?.preventDefault?.();
    const u = usn.trim();
    if (!u) return;
    setScanning(true);
    try {
      const { data } = await apiClient.post('/flexi/jw-orders/receive-usn', { usn: u });
      toast.success(`Received ${u} → ${data.jw_id} (${data.receipted_qty}/${data.sent_qty})`);
      setSummary((prev) => ({
        ...prev,
        [data.jw_id]: {
          assembly_item: data.assembly_item,
          sent_qty: data.sent_qty,
          receipted_qty: data.receipted_qty,
          returned_component_qty: data.returned_component_qty,
          balance_qty: data.balance_qty,
          session: (prev[data.jw_id]?.session || 0) + 1,
        },
      }));
      setLog((prev) => [{ usn: u, jw_id: data.jw_id, component: data.component_item, at: new Date().toLocaleTimeString('en-IN') }, ...prev]);
      setUsn('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Receive failed');
      setUsn('');
    } finally {
      setScanning(false);
      setTimeout(() => usnRef.current?.focus(), 40);
    }
  };

  const summaryList = Object.entries(summary);

  return (
    <StaffScanGate stationName="Flexi — Job Order Receipt">
      <div className="min-h-screen bg-[#FFFBF5]" data-testid="jw-receipt-station">
        <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
          <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/sections/flexi')}
                className="text-[#F9FAFB] hover:bg-white/10">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <PackageCheck className="w-7 h-7" />
                <div>
                  <h1 className="text-xl font-bold font-display">Job Order Receipt</h1>
                  <p className="text-sm text-[#E8DCC8]">Scan assembled (Replace) USNs to receive them back</p>
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate('/sections/flexi/jw-report')}
              className="bg-white/10 border-white/20 text-white hover:bg-white/20"
              data-testid="go-to-report-btn">
              <FileBarChart className="w-4 h-4 mr-2" /> JW Report
            </Button>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-6 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Scan Assembled USN</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={scan} className="flex items-center gap-2">
                <Scan className="w-5 h-5 text-gray-500" />
                <Input ref={usnRef} value={usn} onChange={(e) => setUsn(e.target.value)}
                  placeholder="Scan the assembled piece's Replace USN…"
                  className="h-11 max-w-lg text-base" disabled={scanning}
                  data-testid="receive-usn-input" />
                <Button type="submit" disabled={scanning}
                  className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="receive-usn-submit">
                  {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Receive'}
                </Button>
              </form>
              <p className="text-xs text-gray-500 mt-2">
                The USN auto-routes to its job order and tallies against the pieces sent. Multiple receipts per order are allowed.
              </p>
            </CardContent>
          </Card>

          {summaryList.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {summaryList.map(([jwId, s]) => (
                <Card key={jwId} data-testid={`receipt-summary-${jwId}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm font-semibold">{jwId}</span>
                      <Badge className="bg-emerald-100 text-emerald-700">+{s.session} this session</Badge>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">{s.assembly_item}</p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="bg-gray-50 rounded p-2">
                        <p className="text-[10px] text-gray-500">Order Qty</p>
                        <p className="font-bold" data-testid={`summary-sent-${jwId}`}>{s.sent_qty}</p>
                      </div>
                      <div className="bg-emerald-50 rounded p-2">
                        <p className="text-[10px] text-emerald-600">Receipted</p>
                        <p className="font-bold text-emerald-700" data-testid={`summary-recv-${jwId}`}>{s.receipted_qty}</p>
                      </div>
                      <div className={`rounded p-2 ${s.balance_qty === 0 ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                        <p className={`text-[10px] ${s.balance_qty === 0 ? 'text-emerald-600' : 'text-amber-600'}`}>Balance</p>
                        <p className={`font-bold ${s.balance_qty === 0 ? 'text-emerald-700' : 'text-amber-700'}`} data-testid={`summary-bal-${jwId}`}>{s.balance_qty}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Received this session ({log.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>USN</TableHead>
                    <TableHead>JW Order</TableHead>
                    <TableHead>Component</TableHead>
                    <TableHead>Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!log.length && (
                    <TableRow><TableCell colSpan={4} className="text-center text-gray-400 py-6">
                      No USNs received yet — scan to begin
                    </TableCell></TableRow>
                  )}
                  {log.map((l, i) => (
                    <TableRow key={`${l.usn}-${i}`}>
                      <TableCell className="font-mono text-xs">{l.usn}</TableCell>
                      <TableCell className="font-mono text-xs">{l.jw_id}</TableCell>
                      <TableCell className="font-mono text-xs">{l.component}</TableCell>
                      <TableCell className="text-xs text-gray-500">{l.at}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </main>
      </div>
    </StaffScanGate>
  );
};

export default JwReceiptStation;
